import React, { useState, useEffect } from "react";
import { message } from "antd";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "../services/auth.service";
import { OTPInput } from "../components/OTPInput";
import pmLogo from "../assets/login/Logo.png";
import bannerImage from "../assets/login/banner.png";
import styles from "./OTPVerificationPage.module.css";

const OTPVerificationPage: React.FC = () => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [countdown]);

  const formatTime = (seconds: number): string => {
    return `${seconds.toString().padStart(2, "0")}s`;
  };

  const handleSubmit = async () => {
    if (otp.length !== 6) {
      message.error("Vui lòng nhập đầy đủ 6 chữ số OTP");
      return;
    }
    if (!email) {
      message.error("Không tìm thấy email. Vui lòng quay lại trang quên mật khẩu.");
      navigate("/forgot-password");
      return;
    }

    setLoading(true);
    try {
      await authService.verifyOTP(email, otp);
      navigate(`/reset-password?email=${encodeURIComponent(email)}`);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Mã OTP không chính xác. Vui lòng thử lại."
      );
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!email) {
      message.error("Không tìm thấy email");
      return;
    }
    if (countdown > 0) return;

    setLoading(true);
    try {
      await authService.forgotPassword(email);
      message.success("Mã OTP mới đã được gửi đến email của bạn");
      setOtp("");
      setCountdown(60);
    } catch (error: any) {
      message.error(
        error?.response?.data?.message || "Không thể gửi lại mã OTP. Vui lòng thử lại."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Left Section - Banner */}
      <div className={styles.leftColumn}>
        <div className={styles.bannerWrapper}>
          <img src={bannerImage} alt="OTP Illustration" className={styles.bannerImage} />
        </div>
      </div>

      {/* Right Section - OTP Form */}
      <div className={styles.rightColumn}>
        <div className={styles.rightCard}>
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className={styles.backButton}
          >
            <ArrowLeft size={18} />
            <span>Quay lại</span>
          </button>

          <div className={styles.formContainer}>
            <img src={pmLogo} alt="Logo" className={styles.logo} />

            <div className={styles.header}>
              <ShieldCheck size={48} className={styles.icon} />
              <h2 className={styles.title}>Xác thực OTP</h2>
              <p className={styles.description}>
                Vui lòng nhập mã xác minh đã được gửi đến email của bạn
              </p>
            </div>

            <div className={styles.otpContainer}>
              <OTPInput value={otp} onChange={setOtp} length={6} />
            </div>

            <div className={styles.resendContainer}>
              {countdown > 0 ? (
                <span className={styles.countdownText}>
                  Gửi lại: {formatTime(countdown)}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className={styles.resendButton}
                >
                  Gửi lại mã
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || otp.length !== 6}
              className={styles.submitButton}
            >
              {loading ? "Đang xử lý..." : "Xác nhận"}
            </button>

            <div className={styles.backToLoginContainer}>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className={styles.backToLoginButton}
              >
                Trở về Đăng nhập
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationPage;